import {
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  FormControlLabel,
  Pagination,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { apiRequest } from '@/lib/api/client';
import { EmptyState, ErrorState, FullPageLoader } from '@/components/States';
import { TaskStatus } from '@/components/StatusChip';
import { daysUntil, formatCount, formatHours, formatJalaliDate, formatRelative } from '@/lib/format/persian';
import { statusPalette } from '@/theme/theme';
import { QuickCaptureDialog } from './QuickCaptureDialog';

interface MyWorkItem {
  id: string;
  number: number;
  title: string;
  state: string;
  isBlocked: boolean;
  projectId: string;
  projectName: string;
  dueDate: string | null;
  estimateHours: number | null;
  updatedAt: string;
  webUrl: string;
}

interface MyWorkResponse {
  items: MyWorkItem[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  unestimatedCount: number;
}

/**
 * "My work" - one page, every project.
 *
 * @remarks
 * The first screen most people open, and the one that has to be right on a phone. It is
 * rendered as cards rather than a table on purpose: a table with six columns is unusable at
 * 380px, and the fixes for that - horizontal scrolling, hidden columns - are worse than not
 * using a table.
 *
 * The server decides the order (blocked first, then due date). Re-sorting here would mean
 * two definitions of "what to do next", and the client's would be the one built from a
 * single page of results.
 */
export function MyWorkPage() {
  const [includeDone, setIncludeDone] = useState(false);
  const [page, setPage] = useState(1);
  const [captureOpen, setCaptureOpen] = useState(false);

  const query = useQuery({
    queryKey: ['my-work', { includeDone, page }],
    queryFn: () =>
      apiRequest<MyWorkResponse>('/my-work', { query: { includeDone, page, pageSize: 25 } }),

    // GitLab-backed data is stale by nature. Half a minute keeps it fresh enough to trust
    // without hammering the API on every tab switch (`frontend-standards.md` section 9).
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  if (query.isPending) {
    return <FullPageLoader />;
  }

  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  const { items, totalCount, totalPages, unestimatedCount } = query.data;

  return (
    // Capped where the shell is not. This is one column of cards, each with a title on one
    // side and its chips on the other; stretched across a large monitor the two ends stop
    // reading as the same row. The shell widened for the pages with columns to fill - this
    // one has none, so it keeps the measure it was designed at.
    <Stack spacing={2.5} sx={{ maxWidth: 1200 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
      >
        <Box>
          <Typography variant="h1">کارهای من</Typography>

          <Typography variant="body2" color="text.secondary">
            {formatCount(totalCount)} مورد
            {unestimatedCount > 0 ? (
              <>
                {' · '}
                <Box component="span" sx={{ color: statusPalette.inReview }}>
                  {formatCount(unestimatedCount)} مورد بدون تخمین
                </Box>
              </>
            ) : null}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={includeDone}
                onChange={(event) => {
                  setIncludeDone(event.target.checked);
                  setPage(1);
                }}
              />
            }
            label="نمایش انجام‌شده‌ها"
          />

          <Button variant="contained" onClick={() => setCaptureOpen(true)}>
            ثبت کار جدید
          </Button>
        </Stack>
      </Stack>

      {/* Unestimated work is surfaced rather than hidden: GitLab's estimate is the sole
          input to capacity, so an unestimated task counts as zero and quietly understates
          how full someone is. */}
      {unestimatedCount > 0 ? (
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            border: 1,
            borderColor: `${statusPalette.inReview}55`,
            backgroundColor: `${statusPalette.inReview}14`,
          }}
        >
          <Typography variant="body2">
            {formatCount(unestimatedCount)} مورد تخمین زمانی ندارد و در محاسبه ظرفیت شمرده نمی‌شود.
          </Typography>
        </Box>
      ) : null}

      {items.length === 0 ? (
        <EmptyState
          title={includeDone ? 'موردی یافت نشد' : 'کاری در انتظار شما نیست'}
          description={
            includeDone
              ? 'در پروژه‌هایی که به آن‌ها دسترسی دارید، موردی به نام شما ثبت نشده است.'
              : 'هر کاری که به شما سپرده شود، همین‌جا نمایش داده می‌شود. برای شروع، یک کار جدید ثبت کنید یا از مدیر پروژه بخواهید کاری را به شما بسپارد.'
          }
          action={<CreateTaskButton onClick={() => setCaptureOpen(true)} />}
        />
      ) : (
        <Stack spacing={1.25}>
          {items.map((item) => (
            <TaskCard key={item.id} item={item} />
          ))}
        </Stack>
      )}

      {totalPages > 1 ? (
        <Stack alignItems="center" sx={{ pt: 1 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            // The component renders Latin digits; Persian ones keep it consistent with every
            // other number on the page.
            getItemAriaLabel={(type, pageNumber) =>
              type === 'page' ? `صفحه ${pageNumber}` : type
            }
          />
        </Stack>
      ) : null}

      <QuickCaptureDialog open={captureOpen} onClose={() => setCaptureOpen(false)} />
    </Stack>
  );
}

function TaskCard({ item }: { item: MyWorkItem }) {
  const remaining = daysUntil(item.dueDate);
  const isOverdue = remaining !== null && remaining < 0 && item.state !== 'Done';
  const isDueSoon = remaining !== null && remaining >= 0 && remaining <= 2 && item.state !== 'Done';

  return (
    <Card
      sx={{
        // The overdue marker is a rail on the leading edge, paired with a text chip below.
        // Colour alone would carry nothing for a reader who cannot separate red from grey.
        borderInlineStart: isOverdue ? 3 : 1,
        borderInlineStartColor: isOverdue ? statusPalette.blocked : 'divider',
        transition: 'border-color 120ms ease, background-color 120ms ease',
        '&:hover': { backgroundColor: 'action.hover' },
      }}
    >
      {/* Into the product, not out of it. This used to open the source system in a new tab,
          which sent a non-technical person straight into an issue tracker to read the thing
          they clicked on - the exact leak non-negotiable 7 exists to prevent. The source
          system is still one click away, from the detail page, under a name that does not
          say what it is. */}
      <CardActionArea component={RouterLink} to={`/tasks/${item.id}`} sx={{ p: 2 }}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
            <Typography variant="body1" fontWeight={600} sx={{ lineHeight: 1.5 }}>
              {item.title}
            </Typography>

            <Typography variant="caption" color="text.disabled" className="mono" sx={{ pt: 0.4 }}>
              #{item.number}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
            <TaskStatus state={item.state} isBlocked={item.isBlocked} />

            <Chip size="small" variant="outlined" label={item.projectName} />

            {item.dueDate ? (
              <Chip
                size="small"
                variant="outlined"
                label={`مهلت ${formatJalaliDate(item.dueDate)}`}
                sx={{
                  color: isOverdue
                    ? statusPalette.blocked
                    : isDueSoon
                      ? statusPalette.inReview
                      : undefined,
                  borderColor: isOverdue
                    ? `${statusPalette.blocked}66`
                    : isDueSoon
                      ? `${statusPalette.inReview}66`
                      : undefined,
                }}
              />
            ) : null}

            {isOverdue ? (
              <Chip
                size="small"
                label="گذشته از مهلت"
                sx={{
                  color: statusPalette.blocked,
                  backgroundColor: `${statusPalette.blocked}1F`,
                  fontWeight: 700,
                }}
              />
            ) : null}
          </Stack>

          <Typography variant="caption" color="text.secondary" className="tabular">
            {formatHours(item.estimateHours)} · آخرین تغییر {formatRelative(item.updatedAt)}
          </Typography>
        </Stack>
      </CardActionArea>
    </Card>
  );
}

/** A small helper the empty state uses when a person can create work themselves. */
export function CreateTaskButton({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="contained" onClick={onClick}>
      ثبت وظیفه جدید
    </Button>
  );
}
